import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FaInstagram, FaSnapchatGhost, FaTiktok, FaUserCircle } from "react-icons/fa";
import mixpanel from "mixpanel-browser";
import { getStaticPage } from "../../services/data/staticPages";
import { selectUser } from "../../store/slice/user";
import SubPageNav from "../../components/SubPageNav";
import SiteFooter from "../../components/SiteFooter";
import { Style } from "./style";

// Backend-driven via the admin "Static Pages" > Models & Influencers page
// (StaticPageController: "hero" + repeating "influencer" blocks, each with
// name/ethnicity/bio/photo + instagram/snapchat/tiktok/influence meta).
const socialHandleUrl = (platform, handle) => {
  const clean = String(handle || "").trim().replace(/^@/, "");
  if (!clean) return null;
  if (platform === "instagram") return `https://instagram.com/${clean}`;
  if (platform === "tiktok") return `https://tiktok.com/@${clean}`;
  if (platform === "snapchat") return `https://snapchat.com/add/${clean}`;
  return null;
};

const ModelsInfluencers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, email, name } = useSelector(selectUser);
  const isArabic = language === "ar";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [apiData, setApiData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchModels = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await getStaticPage("models");
        if (cancelled) return;
        setApiData(res.data.data);
        mixpanel?.track("Models & Influencers", {
          email,
          name: name || "anonymous",
        });
      } catch (err) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchModels();
    return () => {
      cancelled = true;
    };
  }, [email, name]);

  const tr = (item, field) =>
    item?.translation?.[language]?.[field] || item?.translation?.en?.[field] || null;

  const heroItem = apiData?.hero?.[0];
  const heroTitle = tr(heroItem, "title") || t("marketing.modelsInfluencers.title");
  const heroTagline = tr(heroItem, "subtitle") || t("marketing.modelsInfluencers.subtitle");

  const influencers = apiData?.influencer || [];

  return (
    <Box sx={Style.page}>
      <SubPageNav />
      <Box sx={Style.hero}>
        <Typography component="h1" sx={Style.heroTitle(isArabic)}>
          {heroTitle}
        </Typography>
        <Typography sx={Style.subtitle}>{heroTagline}</Typography>
      </Box>

      {loading && (
        <Box sx={Style.loaderWrap}>
          <CircularProgress size={64} color="secondary" />
        </Box>
      )}

      {!loading && error && (
        <Box sx={Style.errorWrap}>
          <Typography sx={Style.errorText}>{t("marketing.modelsInfluencers.loadError")}</Typography>
          <Box component="button" sx={Style.backButton} onClick={() => navigate("/")}>
            {t("marketing.nav.backToHome")}
          </Box>
        </Box>
      )}

      {!loading && !error && influencers.length === 0 && (
        <Box sx={Style.emptyWrap}>
          <Typography sx={Style.emptyText}>{t("marketing.modelsInfluencers.noModelsYet")}</Typography>
        </Box>
      )}

      {!loading && !error && influencers.length > 0 && (
        <Box sx={Style.grid(isArabic)}>
          {influencers.map((item) => {
            const socials = [
              { platform: "instagram", icon: <FaInstagram />, handle: item?.meta?.instagram },
              { platform: "snapchat", icon: <FaSnapchatGhost />, handle: item?.meta?.snapchat },
              { platform: "tiktok", icon: <FaTiktok />, handle: item?.meta?.tiktok },
            ].filter((s) => s.handle);

            return (
              <Box key={item.id} sx={Style.card}>
                <Box sx={Style.photoWrap}>
                  {item.full_image ? (
                    <Box component="img" src={item.full_image} alt={tr(item, "title")} sx={Style.photoImg} />
                  ) : (
                    <FaUserCircle style={Style.photoFallback} />
                  )}
                </Box>
                <Typography component="h3" sx={Style.name(isArabic)}>
                  {tr(item, "title")}
                </Typography>
                {tr(item, "subtitle") && (
                  <Typography sx={Style.ethnicity}>{tr(item, "subtitle")}</Typography>
                )}
                {tr(item, "body") && <Typography sx={Style.bio}>{tr(item, "body")}</Typography>}
                {item?.meta?.influence && (
                  <Typography sx={Style.influence}>{item.meta.influence}</Typography>
                )}
                {socials.length > 0 && (
                  <Box sx={Style.socials}>
                    {socials.map((s) => (
                      <Box
                        key={s.platform}
                        component="a"
                        href={socialHandleUrl(s.platform, s.handle)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={Style.socialLink}
                      >
                        {s.icon}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <SiteFooter />
    </Box>
  );
};

export default ModelsInfluencers;
